import React, { useState } from "react";
import PropTypes from "prop-types";

import { useLazyQuery } from "@apollo/react-hooks";

import Heading from "core/Heading";
import ScrollableTable from "core/ScrollableTable";
import Download from "core/ReusableIcons/Download";
import InsightsModal from "./InsightsModal";

import { headerData } from "./data";

import { GET_MONTHLY_ROYALTY_INSIGHTS } from "./queries";

const MonthlyReport = ({ statements }) => {
    const [isModalOpen, setIsOpenModal] = useState(false);

    const tableHeader = "Monthly Photo Reports";

    const [
        getMonthlyInsights,
        { data: insightData, loading: insightLoading }
    ] = useLazyQuery(GET_MONTHLY_ROYALTY_INSIGHTS);

    const openModalWithInsights = ({ month }) => {
        setIsOpenModal(true);

        getMonthlyInsights({ variables: { month } });
    };

    const formatDollars = ({ value }) => {
        const val = parseFloat(value);
        if (!val) {
            return "--";
        } else {
            return "$" + value;
        }
    };

    const formatData = statements.items.map(invoice => {
        const {
            month,
            totalEarned,
            totalPaid,
            fullResSales,
            insightsExist,
            largeSales,
            freeDownloadCount,
            pdfDownloadUrl
        } = invoice;

        const pdfDownloadInfo = {
            href: pdfDownloadUrl,
            title: <Download size="md" />,
            isDownload: true,
            removeLinkStyling: true
        };

        const isPdfAvailable = pdfDownloadUrl ? pdfDownloadInfo : "n/a";

        const invoiceFormattedForTable = {
            month: month,
            amountCreatorEarned: formatDollars({ value: totalEarned }),
            amountPaidToCreator: formatDollars({ value: totalPaid }),
            fullResSales,
            largeSales,
            freeDownloadCount,
            insights: {},
            pdfDownloadUrl: isPdfAvailable
        };

        if (!insightsExist) {
            invoiceFormattedForTable.insights = "n/a";
        } else {
            invoiceFormattedForTable.insights = {
                onClick: () => {
                    openModalWithInsights({ month });
                },
                title: "Insights"
            };
        }

        return invoiceFormattedForTable;
    });

    // just incase some rare situation the table tries to render without
    // data we prevent that
    const isNoData = statements.items.length === 0;
    if (isNoData) return null;

    const modalData = insightData ? insightData : false;

    return (
        <>
            <Heading size="md" topSpacing="4xl" bottomSpacing="lg" type="h2">
                {tableHeader}
            </Heading>
            <ScrollableTableMemoized
                tableHeaderData={headerData}
                tableBodyData={formatData}
                tableAriaLabel={tableHeader}
            />
            <InsightsModal
                isModalOpen={isModalOpen}
                modalData={modalData}
                handleClose={() => setIsOpenModal(false)}
                insightLoading={insightLoading}
            />
        </>
    );
};

const ScrollableTableMemoized = React.memo(
    ({ tableHeaderData, tableBodyData, tableAriaLabel }) => {
        return (
            <ScrollableTable
                tableHeaderData={tableHeaderData}
                tableBodyData={tableBodyData}
                tableAriaLabel={tableAriaLabel}
            />
        );
    },
    (prevProps, nextProps) => {
        let prev = prevProps.tableBodyData.map(report => report.month);
        let next = nextProps.tableBodyData.map(report => report.month);
        return JSON.stringify(prev) === JSON.stringify(next);
    }
);

MonthlyReport.propTypes = {
    statements: PropTypes.object.isRequired
};

export default MonthlyReport;
